import { type EventItem, validateEventItem } from './events.js';
import { dateKey, parseDateKey } from './dates.js';

/** Minimal RFC 4180 CSV parser (quoted fields, escaped quotes, CRLF). */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let q = false;
  const s = text.replace(/^﻿/, '');
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (q) {
      if (ch === '"') {
        if (s[i + 1] === '"') { field += '"'; i++; } else q = false;
      } else field += ch;
    } else if (ch === '"') q = true;
    else if (ch === ',') { row.push(field); field = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && s[i + 1] === '\n') i++;
      row.push(field); field = '';
      if (row.some((c) => c.trim() !== '')) rows.push(row);
      row = [];
    } else field += ch;
  }
  row.push(field);
  if (row.some((c) => c.trim() !== '')) rows.push(row);
  return rows;
}

export const CSV_FIELDS = ['label', 'date', 'end', 'recurring', 'note'] as const;
export type CsvField = (typeof CSV_FIELDS)[number];
export type CsvMapping = Partial<Record<CsvField, number>>;

export const SAMPLE_CSV = `label,date,end,recurring,note
Mom's birthday,1975-03-14,,yes,Recurring every year on 14 March
Shop closed for stocktake,2027-07-01,2027-07-02,no,Two-day closure
Wedding anniversary,2009-11-21,,yes,
Leap-day birthday,2000-02-29,,yes,Shown per the 29 February policy
Winter promotion,2027-06-01,2027-06-30,no,
`;

/** Guess a mapping from header names. */
export function guessMapping(header: string[]): CsvMapping {
  const m: CsvMapping = {};
  header.forEach((h, i) => {
    const k = h.trim().toLowerCase();
    if (/^(label|name|event|title|description)$/.test(k) && m.label === undefined) m.label = i;
    else if (/^(date|start|start date|from)$/.test(k) && m.date === undefined) m.date = i;
    else if (/^(end|end date|to|until)$/.test(k)) m.end = i;
    else if (/^(recurring|repeat|yearly|annual)$/.test(k)) m.recurring = i;
    else if (/^(note|notes|comment)$/.test(k)) m.note = i;
  });
  return m;
}

/** Accept YYYY-MM-DD, DD/MM/YYYY (South African convention) and YYYY/MM/DD. */
export function normaliseDate(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;
  let r = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/.exec(s);
  if (r) return parseDateKey(dateKey(+r[1], +r[2], +r[3])) ? dateKey(+r[1], +r[2], +r[3]) : null;
  r = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/.exec(s);
  if (r) return parseDateKey(dateKey(+r[3], +r[2], +r[1])) ? dateKey(+r[3], +r[2], +r[1]) : null;
  return null;
}

export interface CsvRowResult {
  row: number; // 1-based data row number in the file (header = row 1)
  status: 'new' | 'duplicate' | 'error';
  message?: string;
  item?: EventItem;
}

const truthy = (s: string | undefined) => /^(y|yes|true|1|x|annual|yearly)$/i.test((s ?? '').trim());

export function eventSignature(e: Pick<EventItem, 'label' | 'type' | 'date' | 'start' | 'end' | 'month' | 'day'>): string {
  return [e.label.trim().toLowerCase(), e.type, e.date ?? '', e.start ?? '', e.end ?? '', e.month ?? '', e.day ?? ''].join('|');
}

/**
 * Turn CSV rows into validated event items, flagging errors and duplicates
 * (against existing events and within the file) for a review screen.
 */
export function csvToEvents(
  rows: string[][],
  mapping: CsvMapping,
  existing: EventItem[],
  makeId: () => string,
  hasHeader = true,
): CsvRowResult[] {
  const seen = new Set(existing.map(eventSignature));
  const results: CsvRowResult[] = [];
  const data = hasHeader ? rows.slice(1) : rows;
  data.forEach((cols, i) => {
    const rowNo = i + (hasHeader ? 2 : 1);
    const get = (f: CsvField) => (mapping[f] === undefined ? '' : (cols[mapping[f]!] ?? '').trim());
    const label = get('label');
    const date = normaliseDate(get('date'));
    const endRaw = get('end');
    const end = endRaw ? normaliseDate(endRaw) : null;
    if (!label) return results.push({ row: rowNo, status: 'error', message: 'Missing label' });
    if (label.length > 120) return results.push({ row: rowNo, status: 'error', message: 'Label longer than 120 characters' });
    if (!date) return results.push({ row: rowNo, status: 'error', message: `Unrecognised date "${get('date')}" (use YYYY-MM-DD or DD/MM/YYYY)` });
    if (endRaw && !end) return results.push({ row: rowNo, status: 'error', message: `Unrecognised end date "${endRaw}"` });
    let item: EventItem;
    if (truthy(get('recurring'))) {
      if (end && end !== date) return results.push({ row: rowNo, status: 'error', message: 'Recurring events cannot be ranges' });
      item = { id: makeId(), label, type: 'recurring', month: +date.slice(5, 7), day: +date.slice(8, 10), origin: 'import' };
    } else if (end && end !== date) item = { id: makeId(), label, type: 'range', start: date, end, origin: 'import' };
    else item = { id: makeId(), label, type: 'date', date, origin: 'import' };
    const note = get('note');
    if (note) item.note = note.slice(0, 500);
    const err = validateEventItem(item);
    if (err) return results.push({ row: rowNo, status: 'error', message: err });
    const sig = eventSignature(item);
    if (seen.has(sig)) return results.push({ row: rowNo, status: 'duplicate', message: 'Same label and date already exists', item });
    seen.add(sig);
    results.push({ row: rowNo, status: 'new', item });
  });
  return results;
}
